import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "../bingo-shared/display";
import { selectPageLiteMode, useHallStore } from "./store";
import { hallTheme, type HallThemeId } from "./themes";

const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif';

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  mark: string;
  size: number;
  speed: number;
  drift: number;
  spin: number;
  rot: number;
  bob: number;
  alpha: number;
  burst: boolean;
};

function pickMark(marks: readonly string[]) {
  return marks[Math.floor(Math.random() * marks.length)] ?? "✨";
}

export function HallAtmosphere({ theme, burst = false }: { theme: HallThemeId; burst?: boolean }) {
  const liteMode = useHallStore(selectPageLiteMode);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const burstRef = useRef(burst);
  burstRef.current = burst;

  useEffect(() => {
    if (liteMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reduced = prefersReducedMotion();
    const marks = hallTheme(theme).marks;
    const wide = typeof window !== "undefined" && window.innerWidth >= 700;
    const count = wide ? 26 : 18;
    const particles: Particle[] = [];

    let width = 0;
    let height = 0;
    let bursting = false;

    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const spawnFloater = (partial?: boolean): Particle => ({
      x: Math.random() * Math.max(1, width),
      y: partial ? height + 36 : Math.random() * Math.max(1, height),
      vx: 0,
      vy: 0,
      mark: pickMark(marks),
      size: Math.random() * 22 + 28,
      speed: Math.random() * 0.45 + 0.22,
      drift: (Math.random() - 0.5) * 0.55,
      spin: (Math.random() - 0.5) * 0.02,
      rot: Math.random() * Math.PI * 2,
      bob: Math.random() * Math.PI * 2,
      alpha: Math.random() * 0.28 + 0.55,
      burst: false,
    });

    const spawnBurst = () => {
      const n = wide ? 48 : 36;
      for (let i = 0; i < n; i++) {
        const angle = (Math.PI * 2 * i) / n + Math.random() * 0.4;
        const mag = 4.2 + Math.random() * 7.5;
        particles.push({
          x: width * (0.35 + Math.random() * 0.3),
          y: height * (0.42 + Math.random() * 0.2),
          vx: Math.cos(angle) * mag,
          vy: Math.sin(angle) * mag - 2.4,
          mark: pickMark(marks),
          size: Math.random() * 34 + 38,
          speed: 0,
          drift: 0,
          spin: (Math.random() - 0.5) * 0.12,
          rot: Math.random() * Math.PI * 2,
          bob: 0,
          alpha: 0.95,
          burst: true,
        });
      }
    };

    for (let i = 0; i < count; i++) particles.push(spawnFloater());

    let raf = 0;
    let last = performance.now();
    const draw = (now: number) => {
      if (document.visibilityState === "hidden") {
        raf = 0;
        return;
      }
      const dt = Math.min(2.2, (now - last) / 16.67);
      last = now;
      if (burstRef.current && !bursting) {
        bursting = true;
        spawnBurst();
      }
      if (!burstRef.current && bursting) bursting = false;

      ctx.clearRect(0, 0, width, height);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        if (!p) continue;
        if (!reduced) {
          if (p.burst) {
            p.vy += 0.12 * dt;
            p.x += p.vx * dt * 1.15;
            p.y += p.vy * dt * 1.15;
            p.rot += p.spin * dt * 4;
            p.alpha -= 0.008 * dt;
            if (p.alpha <= 0.04 || p.y > height + 80) {
              particles.splice(i, 1);
              continue;
            }
          } else {
            p.bob += 0.03 * dt;
            p.rot += p.spin * dt;
            p.y -= p.speed * dt;
            p.x += p.drift * dt + Math.sin(p.bob) * 0.35;
            if (p.y < -48) {
              const next = spawnFloater(true);
              p.x = next.x;
              p.y = next.y;
              p.mark = next.mark;
            }
            if (p.x < -40) p.x = width + 40;
            if (p.x > width + 40) p.x = -40;
          }
        }
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.font = `${p.size}px ${EMOJI_FONT}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(p.mark, 0, 0);
        ctx.restore();
      }
      raf = requestAnimationFrame(draw);
    };

    const onResize = () => resize();
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        return;
      }
      last = performance.now();
      if (!raf) raf = requestAnimationFrame(draw);
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVis);
    if (document.visibilityState !== "hidden") raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [theme, liteMode]);

  if (liteMode) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`v2-atmosphere pointer-events-none absolute inset-0 h-full w-full ${
        burst ? "z-[35]" : "z-0"
      }`}
      aria-hidden
    />
  );
}
